import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import isEqual from 'lodash/isEqual'
import { useCreditCard } from '../../CreditCardProvider/CreditCardProvider'
import { Button, ButtonType, ButtonVariant } from '@local/do-secundo-button'
import { ConfirmModal } from '../../ConfirmModal/ConfirmModal'
import { ErrorComponent } from '@local/do-secundo-error'

import AMEX from '../../../assets/credit-cards/amex.svg'
import GENERIC from '../../../assets/credit-cards/credit-card.svg'
import DISCOVER from '../../../assets/credit-cards/discover.svg'
import MASTERCARD from '../../../assets/credit-cards/mastercard.svg'
import VISA from '../../../assets/credit-cards/visa.svg'

import styles from './SavedCreditCardLabel.module.css'
import { toast } from 'react-toastify'

const cardTypeMap = {
  AMEX,
  DISCOVER,
  MASTERCARD,
  VISA
}

export const SavedCreditCardLabel = React.memo(({ card, isDeletable }) => {
  const [showModal, setShowModal] = React.useState(false)
  const { deleteCard, loading } = useCreditCard()
  const CardIcon = cardTypeMap[card.cardType] || GENERIC
  const input = {
    variables: {
      input: {
        cardGuid: card.guid
      }
    }
  }

  const { expired, isPrimary } = card
  const expirationString = expired ? 'Expired' : 'Expires'
  return (
    <span className={cx(styles.card, { [styles.expired]: expired })}>
      <div data-testid='credit-card-info' className={styles.cardInfo}>
        <CardIcon className={styles.cardIcon} />
        <div>
          <p data-testid='credit-card-number' className={styles.cardNumber}>
            {card.maskedPan.replace(/XXXX-/, '**** ')}
          </p>
          <p data-testid='credit-card-exp' className={styles.cardExp}>
            <strong>{expirationString}</strong> {card.expirationMonth}/
            {card.expirationYear}
          </p>
        </div>
      </div>
      {isPrimary && (
        <div data-testid='credit-card-primary' className={styles.primary}>
          Primary
        </div>
      )}
      {isDeletable && (
        <Button
          data-testid='button-delete-cc'
          type={ButtonType.BUTTON}
          onClick={(event) => {
            event.stopPropagation()
            setShowModal(true)
          }}
        >
          DELETE
        </Button>
      )}
      {showModal && (
        <ConfirmModal
          header='Delete Credit Card'
          onClose={() => setShowModal(false)}
          onConfirm={async () => {
            await deleteCard.mutate(input)
            toast('Card Deleted Successfully!')
          }}
          confirmText='Delete Credit Card'
          isLoading={Boolean(loading)}
        >
          {deleteCard.result.error && (
            <ErrorComponent error={deleteCard.result.error} />
          )}
          <div>Are you sure you want to delete this card?</div>
          {isPrimary && <div>This is currently your primary credit card!</div>}
        </ConfirmModal>
      )}
    </span>
  )
}, isEqual)

const CardShape = PropTypes.shape({
  cardType: PropTypes.string.isRequired,
  expirationMonth: PropTypes.string.isRequired,
  expirationYear: PropTypes.string.isRequired,
  isPrimary: PropTypes.bool,
  maskedPan: PropTypes.string.isRequired,
  expired: PropTypes.bool.isRequired
})

SavedCreditCardLabel.propTypes = {
  card: CardShape.isRequired,
  isDeletable: PropTypes.bool
}
